import { recoveryIdentityMatches, type RecoveryMutation } from "./cartRecovery";

export type RecoveryMutationResult = { ok: boolean; reason?: string };
/** An explicit rejection is not a lost write. Unrecognized results/errors remain uncertain. */
export function recoveryReceiptDisposition(result: RecoveryMutationResult): "persisted" | "ineligible" | "uncertain" {
  if (result.ok === true) return "persisted";
  return ["identity_changed", "disabled", "ineligible", "stale_eligibility_generation", "empty",
    "country_required", "purchased_or_ambiguous", "terminal_or_foreign"].includes(result.reason ?? "")
    ? "ineligible" : "uncertain";
}

/** No status subscriber or retry/backfill: every immutable receipt is consumed at most once. */
export function createRecoveryReceiptQueue(deps: {
  owner: string; currentOwner: () => string | null; currentRevision?: () => number;
  record: (event: RecoveryMutation) => Promise<RecoveryMutationResult>;
  quarantine: (input: { cartId: string; eligibilityGeneration: string | null }) => Promise<unknown>;
}) {
  let active = true;
  let queue: Promise<void> = Promise.resolve();
  const allowed = (event: RecoveryMutation) => active &&
    recoveryIdentityMatches(event.initiatingUserId, deps.owner) &&
    recoveryIdentityMatches(event.initiatingUserId, deps.currentOwner()) &&
    (!deps.currentRevision || event.initiatingIdentityRevision === deps.currentRevision());
  return {
    accept(input: RecoveryMutation) {
      if (!allowed(input)) return queue;
      const event = { ...input, lines: input.lines.map((line) => ({ ...line })) };
      queue = queue.then(async () => {
        if (!allowed(event)) return;
        let uncertain = false;
        try { uncertain = recoveryReceiptDisposition(await deps.record(event)) === "uncertain"; }
        catch { uncertain = true; }
        if (!uncertain || !allowed(event)) return;
        // The server atomically quarantines only an EXISTING at-risk episode for this
        // receipt's generation, never creates a terminal reference for a fresh cart.
        await deps.quarantine({ cartId: event.cartId, eligibilityGeneration: event.eligibilityGeneration }).catch(() => undefined);
      });
      return queue;
    },
    dispose() { active = false; },
  };
}